import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {NucleotideType} from 'biojs/BioConstants';
import {sanitize, sanitizeAminoAcids, stripDegenerate} from 'biojs/Util';
import {ActionMethod, ActionOrigin, MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {POLYMER_SUBTYPE_TO_POLYMER_TYPE, PolymerSubtype} from 'constants/MolbioConstants';
import {Section} from 'constants/NavConstants';
import {clearPolymerSearchViewSearchParams} from 'molbio/BioentitySearchView';
import AddPolymerModal from 'molbio/bulk-assembly/add-polymers/AddPolymerModal';
import AddPolymersSearchModal from 'molbio/bulk-assembly/add-polymers/AddPolymersSearchModal';
import AddPolymersWorklistModal from 'molbio/bulk-assembly/add-polymers/AddPolymersWorklistModal';
import {AssemblyCoordinator} from 'molbio/bulk-assembly/AssemblyCoordinator';
import {
  BulkAssemblyBinType,
  BulkAssemblyMethod,
  DisplayBin,
  DisplayFragmentBin,
  FragmentProductionMethod,
  isSpacerSupportingBulkAssemblyType,
  MAX_TABLE_SIZE,
  METHOD_TO_SPACER_NAME,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {baId} from 'molbio/bulk-assembly/BulkAssemblyValues';
import {
  BulkAssemblyCardType,
  BulkAssemblyInputCardProps,
  isBinCard,
} from 'molbio/bulk-assembly/card/BulkAssemblyCard';
import BulkAssemblyCardLayout from 'molbio/bulk-assembly/card/BulkAssemblyCardLayout';
import {AddBinDropdown} from 'molbio/bulk-assembly/overview-tab/AddBinDropdown';
import EditConfirmationModal from 'molbio/bulk-assembly/overview-tab/BulkAssemblyEditConfirmationModal';
import {DestructiveActionsForApproval} from 'molbio/bulk-assembly/state/BulkAssemblyStateManager';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {HoverPosition} from 'ui-library/drag-and-drop/hooks/useDnDCanvasDragState';
import legacyRunModal from 'ui-library/legacyRunModal';
import {capitalize} from 'util/NameUtil';

function onChangeBinFragmentProductionMethod(
  assemblyCoordinator: AssemblyCoordinator,
  displayBin: DisplayFragmentBin,
  fragmentProductionMethod: FragmentProductionMethod
): Promise<void> {
  return assemblyCoordinator.updateBinFragmentProductionMethod(
    displayBin.cid,
    fragmentProductionMethod,
    async (destructiveActions: DestructiveActionsForApproval) =>
      Boolean(
        await legacyRunModal(EditConfirmationModal, {
          actionText: 'Are you sure you want to continue?',
          warnings: Object.values(destructiveActions).flat(),
          title: 'Warning',
        })
      )
  );
}

/**
 * Builds props card component props for each bin using the assembly coordinator.
 *
 * @param assemblyCoordinator AssemblyCoordinator instance.
 */
export function getCardProps(assemblyCoordinator: AssemblyCoordinator): Array<BulkAssemblyInputCardProps> {
  const {errorsByBin} = assemblyCoordinator.assemblyLayoutValidator.getAssemblyLayoutValidationErrors();
  const displayBins = assemblyCoordinator.getBinsForDisplay();
  const totalFragmentBins = displayBins.filter(
    ({binType}) => binType === BulkAssemblyBinType.FRAGMENT
  ).length;
  const bulkAssemblyMethodData = assemblyCoordinator.assemblySelector.getMethod();
  const supportedPolymerTypes = assemblyCoordinator.supportedPolymerSubtypes();
  let runningFragmentBinCount = 0;
  let runningSpacerCount = 0;

  return displayBins.map((displayBin: DisplayBin, index): BulkAssemblyInputCardProps => {
    const commonProps = {
      cid: displayBin.cid,
      name: displayBin.name,
      onChangeName: (name: string): void => {
        analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_BIN_NAME, {
          actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_BIN_LAYOUT,
          assemblyId: assemblyCoordinator.assemblyId,
        });
        assemblyCoordinator.updateBinName(displayBin.cid, name);
      },
      index,
      errors: [...errorsByBin[displayBin.cid]],
      isReadOnly: assemblyCoordinator.isReadOnly(),
      supportedPolymerTypes,
    };

    const getOnDeleteCallback = (event: analytics.AnalyticsEvent) => () => {
      analytics.trackEvent(event, {
        actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_BIN_LAYOUT,
        assemblyId: assemblyCoordinator.assemblyId,
      });
      assemblyCoordinator.deleteBin(index);
    };

    const maxNumEntitiesToAdd = MAX_TABLE_SIZE - assemblyCoordinator.state.fragments.length;

    if (displayBin.binType === BulkAssemblyBinType.FRAGMENT) {
      const enableSelectPreferredPrimers = assemblyCoordinator.enableSelectPreferredPrimers(
        baId(displayBin.cid)
      );
      const constructFragmentIndex = assemblyCoordinator.assemblySelector
        .getFragmentBins()
        .findIndex((bin) => bin.cid === displayBin.cid);
      const binIndex = assemblyCoordinator.getBinCidOrdering().findIndex((cid) => cid === displayBin.cid);

      return {
        ...commonProps,
        cardTypeIndex: runningFragmentBinCount++,
        cardType: BulkAssemblyCardType.BIN,
        hideDeleteButton: totalFragmentBins <= 2,
        lockName: false,
        bulkAssemblyMethod: bulkAssemblyMethodData.type,
        fragmentProductionMethod: displayBin.fragmentProductionMethod,
        onChangeFragmentProductionMethod: (fragmentProductionMethod) => {
          onChangeBinFragmentProductionMethod(assemblyCoordinator, displayBin, fragmentProductionMethod);
        },
        numContained: displayBin.numBinFragments,
        color: displayBin.color,
        fragmentIcon: assemblyCoordinator.getFragmentIcon(),
        supportedPolymerTypes,
        onDelete: getOnDeleteCallback(MOLBIO_ANALYTICS_ACTIONS.DELETED_BULK_ASSEMBLY_BIN),
        openAddPolymerModal: async (polymerId: string) => {
          analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.OPENED_BULK_ASSEMBLY_ADD_FRAGMENTS_MODAL, {
            actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_BIN_LAYOUT,
            assemblyId: assemblyCoordinator.assemblyId,
            actionMethod: ActionMethod.ADD_FROM_RECENT,
          });
          const fragments = await legacyRunModal(AddPolymerModal, {
            assemblyId: assemblyCoordinator.assemblyId,
            binCid: displayBin.cid,
            binColor: displayBin.color,
            binIndex,
            bulkAssemblyMethodData,
            canSpecifyEndpoints: assemblyCoordinator.canSpecifyEndpoints(),
            canUpdateRestrictionEnzymes: assemblyCoordinator.canUpdateRestrictionEnzymes(displayBin.cid),
            constructFragmentIndex,
            constructTopology: assemblyCoordinator.topology,
            enableSelectPreferredPrimers,
            fragmentPolymerId: polymerId,
            fragmentProductionMethod: displayBin.fragmentProductionMethod,
            numConstructFragments: assemblyCoordinator.assemblySelector.getNumFragmentBins(),
            polymerSubtypes: supportedPolymerTypes,
          });
          if (fragments) {
            assemblyCoordinator.addFragments(fragments);
            analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.ADDED_BULK_ASSEMBLY_FRAGMENTS, {
              actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_OVERVIEW_TAB,
              assemblyId: assemblyCoordinator.assemblyId,
              actionMethod: ActionMethod.ADD_FROM_RECENT,
              numFragments: fragments.length,
            });
          }
        },
        openAddPolymersSearchModal: async () => {
          analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.OPENED_BULK_ASSEMBLY_ADD_FRAGMENTS_MODAL, {
            actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_BIN_LAYOUT,
            assemblyId: assemblyCoordinator.assemblyId,
            actionMethod: ActionMethod.ADD_FROM_SEARCH,
          });
          clearPolymerSearchViewSearchParams(supportedPolymerTypes, Section.MOLBIO_BULK_ASSEMBLY);
          const fragments = await legacyRunModal(AddPolymersSearchModal, {
            assemblyId: assemblyCoordinator.assemblyId,
            binCid: displayBin.cid,
            binColor: displayBin.color,
            binIndex,
            bulkAssemblyMethodData,
            canSpecifyEndpoints: assemblyCoordinator.canSpecifyEndpoints(),
            canUpdateRestrictionEnzymes: assemblyCoordinator.canUpdateRestrictionEnzymes(displayBin.cid),
            constructFragmentIndex,
            constructTopology: assemblyCoordinator.topology,
            enableSelectPreferredPrimers,
            fragmentProductionMethod: displayBin.fragmentProductionMethod,
            maxNumEntitiesToAdd,
            numConstructFragments: assemblyCoordinator.assemblySelector.getNumFragmentBins(),
            polymerSubtypes: supportedPolymerTypes,
          });
          if (fragments) {
            assemblyCoordinator.addFragments(fragments);
            analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.ADDED_BULK_ASSEMBLY_FRAGMENTS, {
              actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_OVERVIEW_TAB,
              assemblyId: assemblyCoordinator.assemblyId,
              actionMethod: ActionMethod.ADD_FROM_SEARCH,
              numFragments: fragments.length,
            });
          }
        },
        openAddPolymersWorklistModal: async () => {
          analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.OPENED_BULK_ASSEMBLY_ADD_FRAGMENTS_MODAL, {
            actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_BIN_LAYOUT,
            assemblyId: assemblyCoordinator.assemblyId,
            actionMethod: ActionMethod.ADD_FROM_WORKLIST,
          });
          const fragments = await legacyRunModal(AddPolymersWorklistModal, {
            assemblyId: assemblyCoordinator.assemblyId,
            binCid: displayBin.cid,
            binColor: displayBin.color,
            bulkAssemblyMethodData,
            canSpecifyEndpoints: assemblyCoordinator.canSpecifyEndpoints(),
            canUpdateRestrictionEnzymes: assemblyCoordinator.canUpdateRestrictionEnzymes(displayBin.cid),
            constructFragmentIndex,
            constructTopology: assemblyCoordinator.topology,
            enableSelectPreferredPrimers,
            fragmentProductionMethod: displayBin.fragmentProductionMethod,
            numConstructFragments: assemblyCoordinator.assemblySelector.getNumFragmentBins(),
            polymerSubtypes: supportedPolymerTypes,
            binIndex,
          });
          if (fragments) {
            assemblyCoordinator.addFragments(fragments);
            analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.ADDED_BULK_ASSEMBLY_FRAGMENTS, {
              actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_OVERVIEW_TAB,
              assemblyId: assemblyCoordinator.assemblyId,
              actionMethod: ActionMethod.ADD_FROM_WORKLIST,
              numFragments: fragments.length,
            });
          }
        },
      };
    } else {
      assert(
        isSpacerSupportingBulkAssemblyType(bulkAssemblyMethodData.type),
        'Received spacer bin for homology assembly'
      );
      return {
        ...commonProps,
        cardType: BulkAssemblyCardType.SPACER,
        cardTypeIndex: runningSpacerCount++,
        bulkAssemblyMethod: bulkAssemblyMethodData.type,
        residues: displayBin.residues,
        onChangeResidues: (residues: string): void => {
          analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_SPACER_SETTINGS, {
            actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_BIN_LAYOUT,
            assemblyId: assemblyCoordinator.assemblyId,
          });
          assemblyCoordinator.updateSpacerBinBases(displayBin.cid, residues);
        },
        onDelete: getOnDeleteCallback(MOLBIO_ANALYTICS_ACTIONS.DELETED_BULK_ASSEMBLY_SPACER),
        sanitizeResidues: (newResidues: string) => {
          if (bulkAssemblyMethodData.type === BulkAssemblyMethod.CONCATENATION) {
            if (bulkAssemblyMethodData.parameters.polymerSubtype === PolymerSubtype.aa_sequence) {
              return sanitizeAminoAcids(newResidues);
            }
            const supportsUTFlexibility =
              BenchlingConfig.MOLBIO_ENABLE_UT_FLEXIBILITY &&
              (bulkAssemblyMethodData.parameters.polymerSubtype === PolymerSubtype.dna_oligo ||
                bulkAssemblyMethodData.parameters.polymerSubtype === PolymerSubtype.rna_oligo);
            return sanitize(
              newResidues,
              POLYMER_SUBTYPE_TO_POLYMER_TYPE[
                bulkAssemblyMethodData.parameters.polymerSubtype
              ] as NucleotideType,
              supportsUTFlexibility
            );
          } else {
            return stripDegenerate(newResidues, NucleotideType.DNA);
          }
        },
      };
    }
  });
}

export default function BulkAssemblyCardSection({
  assemblyCoordinator,
}: {
  assemblyCoordinator: AssemblyCoordinator;
}): JSX.Element {
  const binCount = assemblyCoordinator.getBinCount();
  const isReadOnly = assemblyCoordinator.isReadOnly();
  const {otherErrors} = assemblyCoordinator.assemblyLayoutValidator.getAssemblyLayoutValidationErrors();
  const bulkAssemblyType = assemblyCoordinator.assemblySelector.getMethod().type;
  const spacerName = isSpacerSupportingBulkAssemblyType(bulkAssemblyType)
    ? METHOD_TO_SPACER_NAME[bulkAssemblyType]
    : undefined;

  return (
    <div>
      <div>
        <div className={css(styles.cardLayoutHeader)}>
          {spacerName != null ? `Bins & ${pluralize(2, capitalize(spacerName))}` : 'Bins'}
          {binCount > 0 ? ` (${binCount})` : ''}
          {!isReadOnly && (
            <AddBinDropdown
              onAddNewFragmentBin={() => {
                assemblyCoordinator.handleAddNewFragmentBin();
                analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.ADDED_BULK_ASSEMBLY_BIN, {
                  actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_BIN_LAYOUT,
                  assemblyId: assemblyCoordinator.assemblyId,
                });
              }}
              onAddNewSpacerBin={() => {
                assemblyCoordinator.handleAddNewSpacerBin();
                analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.ADDED_BULK_ASSEMBLY_SPACER, {
                  actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_BIN_LAYOUT,
                  assemblyId: assemblyCoordinator.assemblyId,
                });
              }}
              fragmentIcon={assemblyCoordinator.getFragmentIcon()}
              spacerName={spacerName}
            />
          )}
        </div>
      </div>
      <div className={css(styles.cardLayoutWrapper)}>
        <BulkAssemblyCardLayout
          cards={getCardProps(assemblyCoordinator)}
          onMoveItem={(
            sourceItem: BulkAssemblyInputCardProps,
            targetItem: BulkAssemblyInputCardProps,
            hoverPosition?: HoverPosition
          ) => {
            const event = isBinCard(sourceItem)
              ? MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_BIN_NUMBER
              : MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_SPACER_NUMBER;
            analytics.trackEvent(event, {
              actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_BIN_LAYOUT,
              assemblyId: assemblyCoordinator.assemblyId,
            });
            assemblyCoordinator.handleMoveBin(sourceItem.index, targetItem.index, hoverPosition);
          }}
          bulkAssemblyMethod={assemblyCoordinator.assemblySelector.getMethod().type}
          constructBinErrors={otherErrors}
          numFilledOutConstructs={assemblyCoordinator.getNumFilledOutConstructs()}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  cardLayoutHeader: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    display: 'flex',
    alignItems: 'center',
  },
  cardLayoutWrapper: {
    marginLeft: '-12px',
  },
});
